<script setup>
// Follow-up: o agente voltando a falar com quem parou de responder.
//
// É a única parte do produto que manda mensagem para quem NÃO escreveu, e a tela é
// desenhada em torno disso. Três decisões visíveis aqui:
//
//   1. a janela de horário nasce LIGADA e diz na cara qual é (5h–22h no fuso do agente).
//      Um padrão que protege e não aparece é um padrão que ninguém sabe que tem — e quando
//      alguém desligar, vai ser de propósito;
//   2. o teto de três estágios não é um limite de campo, é o desenho. Insistir uma quarta
//      vez com quem não respondeu três é onde follow-up vira incômodo;
//   3. as frases de encerramento são DECLARADAS pelo cliente. "Formulário enviado com
//      sucesso" é da BMC; virar regra da plataforma transformaria o caso de um cliente em
//      regra de todos.
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';

const props = defineProps({ agent: { type: Object, required: true } });

const { t } = useI18n();
const ctx = inject('powerAgentContext');

// O mesmo teto do runtime e do controller. Está nos três lugares de propósito: o campo é
// conveniência, a API é a fronteira, e o runtime é quem de fato não manda a quarta.
const MAX_ESTAGIOS = 3;
const JANELA_PADRAO = { inicio: 5, fim: 22 };
// O piso de cada estágio, igual ao da API e ao do runtime. O campo impede antes de o Save
// ir; a API recusa de qualquer jeito, porque a tela é contornável.
const MINUTOS_MINIMOS = 5;
// Mesmo teto do runtime e da API. Cada condição é um pedaço do prompt do modelo que decide;
// passar de dez transforma a decisão numa leitura de regulamento.
const MAX_CONDICOES = 10;
// Os atrasos que um estágio novo sugere. Meia hora, meio dia, um dia — a cadência que a Eva
// usa hoje, e não um número redondo inventado na hora.
const SUGESTOES = [30, 480, 1440];

const fu = props.agent.config?.followup || {};

const ligado = ref(Boolean(fu.enabled));
const estagios = ref(
  (fu.stages || [])
    .slice(0, MAX_ESTAGIOS)
    .map(e => ({ minutos: e.after_minutes }))
);
// `!== false` e não `|| true`: a diferença é entre "nunca configurado" (vem ligado) e
// "desligado de propósito" (respeita). `|| true` religaria a janela de quem a desligou.
const horarioLigado = ref(fu.hours_enabled !== false);
const horaInicio = ref(fu.hour_start ?? JANELA_PADRAO.inicio);
const horaFim = ref(fu.hour_end ?? JANELA_PADRAO.fim);
// As condições substituíram as listas de frase. Uma lista de substring só pega o que o
// cliente adivinhou escrever — "deixa pra depois" não pega "vou ver com meu marido e te
// falo", que é a mesma situação. A condição descreve a SITUAÇÃO, e quem a avalia é um modelo
// pequeno na hora de decidir.
const condicoes = ref(
  (fu.conditions || []).map(c => ({
    texto: c.text || '',
    acao: c.action || 'adiar',
  }))
);
const saving = ref(false);

const fuso = computed(
  () => props.agent.config?.context_timezone || 'America/Sao_Paulo'
);

const podeAdicionar = computed(() => estagios.value.length < MAX_ESTAGIOS);
const podeAdicionarCondicao = computed(
  () => condicoes.value.length < MAX_CONDICOES
);

// Os exemplos existem porque condição em branco é a tela mais difícil de começar a usar: sem
// ver uma escrita, ninguém sabe se deve escrever "cliente comprou" ou um parágrafo.
const EXEMPLOS = [
  { texto: 'o cliente já comprou o que estava buscando', acao: 'encerrar' },
  { texto: 'o cliente pediu para retornar em outra data', acao: 'adiar' },
  {
    texto: 'é um fornecedor ou candidato a vaga, não um cliente',
    acao: 'encerrar',
  },
];

function adicionarCondicao(modelo) {
  if (!podeAdicionarCondicao.value) return;
  condicoes.value.push(modelo ? { ...modelo } : { texto: '', acao: 'adiar' });
}

function removerCondicao(i) {
  condicoes.value.splice(i, 1);
}

// A janela que cruza a meia-noite é legítima (22h às 5h, plantão noturno) e o runtime a
// trata como união. A tela precisa dizer isso, senão parece campo trocado.
const janelaCruzaMeiaNoite = computed(
  () => Number(horaInicio.value) > Number(horaFim.value)
);

function adicionarEstagio() {
  if (!podeAdicionar.value) return;
  estagios.value.push({ minutos: SUGESTOES[estagios.value.length] || 1440 });
}

function removerEstagio(i) {
  estagios.value.splice(i, 1);
}

// Minutos são o que a API guarda; horas e dias são como as pessoas pensam. A tradução fica
// na tela, do lado de quem lê — o formato guardado não muda.
function emPalavras(minutos) {
  const m = Number(minutos);
  if (!m || m <= 0) return '';
  if (m < 60) return t('POWER_AGENTS.FOLLOWUP.IN_MINUTES', { n: m });
  if (m < 1440) {
    return t('POWER_AGENTS.FOLLOWUP.IN_HOURS', { n: +(m / 60).toFixed(1) });
  }
  return t('POWER_AGENTS.FOLLOWUP.IN_DAYS', { n: +(m / 1440).toFixed(1) });
}

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  ligado: ligado.value,
  estagios: estagios.value,
  horarioLigado: horarioLigado.value,
  horaInicio: horaInicio.value,
  horaFim: horaFim.value,
  condicoes: condicoes.value,
}));

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          followup: {
            enabled: ligado.value,
            stages: estagios.value
              .map(e => ({ after_minutes: Number(e.minutos) }))
              .filter(e => e.after_minutes >= MINUTOS_MINIMOS),
            hours_enabled: horarioLigado.value,
            hour_start: Number(horaInicio.value),
            hour_end: Number(horaFim.value),
            conditions: condicoes.value
              .map(c => ({ text: c.texto.trim(), action: c.acao }))
              .filter(c => c.text),
          },
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Follow-up é comportamento do agente e só passa a valer quando a revisão é publicada.
    // Sem isto, quem salva acha que ligou — e o agente segue calado até alguém publicar.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);
</script>

<template>
  <SectionShell
    :title="t('POWER_AGENTS.SECTIONS.FOLLOWUP')"
    :description="t('POWER_AGENTS.FOLLOWUP.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <div data-busca="followup-enabled" class="flex items-start gap-3 p-1">
      <Switch v-model="ligado" class="mt-0.5" />
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.FOLLOWUP.ENABLED') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.FOLLOWUP.ENABLED_HINT') }}
        </span>
      </div>
    </div>

    <!-- Estágios -->
    <div data-busca="followup-stages">
      <div class="flex items-center justify-between gap-3">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.FOLLOWUP.STAGES') }}
          </h3>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{
              t('POWER_AGENTS.FOLLOWUP.STAGES_HINT', {
                max: MAX_ESTAGIOS,
                min: MINUTOS_MINIMOS,
              })
            }}
          </p>
        </div>
        <!-- shrink-0 e whitespace-nowrap: sem os dois o flex espreme o botão e o rótulo
             vira "Add s…", que foi como ele nasceu. -->
        <Button
          icon="i-lucide-plus"
          :label="t('POWER_AGENTS.FOLLOWUP.ADD_STAGE')"
          slate
          faded
          sm
          class="shrink-0 whitespace-nowrap"
          :disabled="!podeAdicionar"
          @click="adicionarEstagio"
        />
      </div>

      <p
        v-if="!estagios.length"
        class="p-3 mt-2 text-xs border border-dashed rounded-lg border-n-weak text-n-slate-10"
      >
        {{ t('POWER_AGENTS.FOLLOWUP.NO_STAGES') }}
      </p>

      <div v-else class="flex flex-col gap-2 mt-2">
        <div
          v-for="(estagio, i) in estagios"
          :key="i"
          class="flex flex-wrap items-center gap-3 p-3 border rounded-lg sm:flex-nowrap border-n-weak"
        >
          <span
            class="grid w-6 h-6 text-xs font-semibold rounded-full shrink-0 place-items-center bg-n-alpha-2 text-n-slate-11"
          >
            {{ i + 1 }}
          </span>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            {{ t('POWER_AGENTS.FOLLOWUP.AFTER') }}
            <input
              v-model="estagio.minutos"
              type="number"
              :min="MINUTOS_MINIMOS"
              class="reset-base w-24 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
            />
            {{ t('POWER_AGENTS.FOLLOWUP.MINUTES') }}
          </label>
          <span class="text-xs text-n-slate-10">{{
            emPalavras(estagio.minutos)
          }}</span>
          <Button
            icon="i-lucide-trash-2"
            ruby
            faded
            sm
            class="ml-auto"
            :label="t('POWER_AGENTS.FOLLOWUP.REMOVE')"
            @click="removerEstagio(i)"
          />
        </div>
      </div>
    </div>

    <!-- A janela de horário. Ela ocupa uma caixa própria e diz que está ligada por padrão,
         porque um padrão que protege sem aparecer é um padrão que ninguém sabe que tem. -->
    <div
      data-busca="followup-hours"
      class="p-4 border rounded-xl border-n-weak bg-n-alpha-1"
    >
      <div class="flex items-start gap-3">
        <Switch v-model="horarioLigado" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.FOLLOWUP.HOURS') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{
              t('POWER_AGENTS.FOLLOWUP.HOURS_HINT', {
                inicio: JANELA_PADRAO.inicio,
                fim: JANELA_PADRAO.fim,
                fuso: fuso,
              })
            }}
          </span>
        </div>
      </div>

      <div v-if="horarioLigado" class="flex flex-wrap items-end gap-3 mt-3">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.FOLLOWUP.FROM_HOUR') }}
          <input
            v-model="horaInicio"
            type="number"
            min="0"
            max="23"
            class="reset-base w-20 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ t('POWER_AGENTS.FOLLOWUP.TO_HOUR') }}
          <input
            v-model="horaFim"
            type="number"
            min="0"
            max="23"
            class="reset-base w-20 px-2 py-1.5 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </label>
        <p class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.FOLLOWUP.TIMEZONE_NOTE', { fuso }) }}
        </p>
      </div>

      <p
        v-if="horarioLigado && janelaCruzaMeiaNoite"
        class="mt-2 text-xs text-n-amber-11"
      >
        {{
          t('POWER_AGENTS.FOLLOWUP.CROSSES_MIDNIGHT', {
            inicio: horaInicio,
            fim: horaFim,
          })
        }}
      </p>

      <!-- Desligar a janela é uma escolha, e ela merece o aviso na hora — não a descoberta
           pelo cliente que recebeu mensagem às quatro da manhã. -->
      <p v-if="!horarioLigado" class="mt-2 text-xs text-n-amber-11">
        {{ t('POWER_AGENTS.FOLLOWUP.HOURS_OFF_WARNING') }}
      </p>
    </div>

    <!-- Condições: o domínio do cliente entra aqui, e só aqui.

         Cada linha é uma frase que descreve uma SITUAÇÃO, não palavras a procurar, e a ação
         ao lado diz o que fazer quando ela for verdade. Quem avalia é um modelo pequeno, na
         hora de decidir — por isso "vou ver com meu marido" cai em "o cliente disse que vai
         pensar" sem ninguém ter previsto essa frase. -->
    <div data-busca="followup-conditions">
      <div class="flex items-center justify-between gap-3">
        <div class="min-w-0">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.FOLLOWUP.CONDITIONS') }}
          </h3>
          <p class="mt-0.5 text-xs text-n-slate-10">
            {{
              t('POWER_AGENTS.FOLLOWUP.CONDITIONS_HINT', { max: MAX_CONDICOES })
            }}
          </p>
        </div>
        <Button
          icon="i-lucide-plus"
          :label="t('POWER_AGENTS.FOLLOWUP.ADD_CONDITION')"
          slate
          faded
          sm
          class="shrink-0 whitespace-nowrap"
          :disabled="!podeAdicionarCondicao"
          @click="adicionarCondicao()"
        />
      </div>

      <!-- Vazio não é um retângulo dizendo "vazio": são os exemplos, clicáveis. Condição em
           branco é a tela mais difícil de começar a usar, porque ninguém sabe se escreve duas
           palavras ou um parágrafo — ver uma escrita resolve isso melhor que qualquer texto
           de ajuda. -->
      <div
        v-if="!condicoes.length"
        class="p-4 mt-2 border border-dashed rounded-lg border-n-weak"
      >
        <p class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.FOLLOWUP.NO_CONDITIONS') }}
        </p>
        <div class="flex flex-wrap gap-2 mt-3">
          <button
            v-for="exemplo in EXEMPLOS"
            :key="exemplo.texto"
            class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs transition-colors border rounded-full border-n-weak text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            @click="adicionarCondicao(exemplo)"
          >
            <i class="text-sm i-lucide-plus text-n-slate-10" />
            {{ exemplo.texto }}
          </button>
        </div>
      </div>

      <div v-else class="flex flex-col gap-2 mt-2">
        <div
          v-for="(condicao, i) in condicoes"
          :key="i"
          class="flex flex-wrap items-center gap-2 p-2 border rounded-lg border-n-weak sm:flex-nowrap"
        >
          <i
            class="text-base shrink-0 ml-1"
            :class="
              condicao.acao === 'encerrar'
                ? 'i-lucide-circle-slash text-n-ruby-9'
                : 'i-lucide-pause text-n-amber-11'
            "
          />
          <input
            v-model="condicao.texto"
            type="text"
            class="reset-base flex-1 min-w-0 px-2 py-1.5 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="t('POWER_AGENTS.FOLLOWUP.CONDITION_PLACEHOLDER')"
          />
          <!-- `w-28` e `mb-0` não são estética: a regra legada `select { width:100%; margin-bottom:1rem }`
               do _base.scss não tem escape (a de input tem `.reset-base`). Sem a largura, este select
               exigia a linha inteira e o input ao lado — `flex-1 min-w-0`, e aqui ainda por cima
               `bg-transparent border-0` — colapsava para ZERO em telas >= 640px, sumindo com o texto
               da condição. Mesmo defeito que apagou o nome da categoria na seção Handoff. -->
          <select
            v-model="condicao.acao"
            class="w-28 mb-0 shrink-0 px-2 py-1.5 text-xs border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-11"
          >
            <option value="adiar">
              {{ t('POWER_AGENTS.FOLLOWUP.ACTION_POSTPONE') }}
            </option>
            <option value="encerrar">
              {{ t('POWER_AGENTS.FOLLOWUP.ACTION_STOP') }}
            </option>
          </select>
          <Button
            icon="i-lucide-x"
            slate
            faded
            xs
            class="shrink-0"
            :label="t('POWER_AGENTS.FOLLOWUP.REMOVE')"
            @click="removerCondicao(i)"
          />
        </div>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.FOLLOWUP.ACTIONS_EXPLAINED') }}
        </p>
      </div>
    </div>
  </SectionShell>
</template>
